/**
 * Whether a Notification Setup row is on — the single definition the row's On
 * switch, the section's "X of Y on" count and the hook's staging rules share.
 *
 * Kept in one place on purpose: the switch, the header count and Enable all
 * used to each read `config.enabled` on their own, so a retired event could
 * read On, be counted On and be bulk-enabled while the platform never sent it
 * (CR-7770), and a row with every channel unticked read On while it delivered
 * nothing at all (CR-7771).
 *
 * @link ./useNotificationSetup for the staging rules that call these.
 */
import type {
  NotificationChannel,
  NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import { isEmailSilenced, resolveFrequency, type EventConfig, type SetupEvent } from './types';

/** Tooltip and accessible text on a retired row's locked On switch. */
export const RETIRED_SWITCH_TEXT = 'Retired – this event is no longer sent';

/**
 * Whether this event may be switched on at all.
 *
 * A retired template (`is_active=false`) is never sent, whatever a tenant
 * override says, and the API refuses `is_enabled=true` for it with
 * `event_retired`. The grid must not offer what the server will refuse.
 *
 * @param event - the grid row.
 * @returns false for a retired event, true for every live one.
 */
export const canTurnOn = (event: SetupEvent): boolean => event.isActive;

/**
 * The ticked channels that can actually deliver at the row's cadence.
 *
 * Only shipped channels count, and the cadence rule is the grid's existing
 * one ({@link isEmailSilenced}): at a frequency of Off only Bell delivers,
 * whatever the Email and Digest ticks say. The cadence resolves the way the
 * backend does — the row's own, else the tenant default, else the platform's.
 *
 * @param event - the grid row.
 * @param config - the config the row renders.
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns the channels that would deliver, in column order.
 */
export const deliverableChannels = (
  event: SetupEvent,
  config: EventConfig,
  tenantDefault: NotificationTenantDefaultFrequency,
): NotificationChannel[] => {
  const silenced = isEmailSilenced(
    resolveFrequency(config.frequency, tenantDefault, event.platformFrequency),
  );
  return event.availableChannels.filter(
    (channel) => config.channels.includes(channel) && (channel === 'bell' || !silenced),
  );
};

/**
 * Whether this row reads On: live, switched on, and with at least one channel
 * that can deliver.
 *
 * A retired row reads Off even when a stale override from before it was
 * retired still says `is_enabled=true`, and a row with nothing left to deliver
 * reads Off even though it is switched on — the backend sends it nowhere, so
 * On would lie either way.
 *
 * @param event - the grid row.
 * @param config - the config the row renders (staged edit, reset or server state).
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns true when the row will actually notify someone.
 */
export const isEventOn = (
  event: SetupEvent,
  config: EventConfig,
  tenantDefault: NotificationTenantDefaultFrequency,
): boolean =>
  canTurnOn(event)
  && config.enabled
  && deliverableChannels(event, config, tenantDefault).length > 0;

/**
 * The config a row takes when it is switched On.
 *
 * Switching On must never leave a row reading On while delivering nothing, so
 * a row with no deliverable channel gets its shipped channels back; if even
 * those cannot deliver at its cadence, it goes back to inheriting, and if the
 * inherited cadence is silent too, it is pinned to its own platform cadence.
 *
 * @param event - the grid row.
 * @param config - its current config.
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns the config to stage. Unchanged apart from `enabled` whenever the
 *   row already has a channel that delivers.
 */
export function switchedOn(
  event: SetupEvent,
  config: EventConfig,
  tenantDefault: NotificationTenantDefaultFrequency,
): EventConfig {
  const delivers = (candidate: EventConfig): boolean =>
    deliverableChannels(event, candidate, tenantDefault).length > 0;
  const on: EventConfig = { ...config, enabled: true };
  if (delivers(on)) return on;
  const shipped: EventConfig = { ...on, channels: event.defaults.channels };
  if (delivers(shipped)) return shipped;
  const inherited: EventConfig = { ...shipped, frequency: event.defaults.frequency };
  if (delivers(inherited)) return inherited;
  return { ...shipped, frequency: event.platformFrequency };
}
